import { io, Socket } from 'socket.io-client';
import { BACKEND_CONFIG } from '../config/backendConfig';

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private token: string | null = null;

  initialize(token: string) {
    if (this.socket) {
      this.socket.disconnect();
    }

    const socketUrl = BACKEND_CONFIG.baseURL.replace('/api', '');
    
    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      // audit #234: never stop trying to reconnect — after a finite cap the badge
      // would silently stop clearing and notifications would stop arriving.
      reconnectionAttempts: Infinity
    });

    this.setupEventListeners();
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => this.emit('connect'));
    this.socket.on('disconnect', () => this.emit('disconnect'));
    this.socket.on('notification', (data: any) => this.emit('notification', data));
    this.socket.on('notification:read', (data: { notificationId: string }) => this.emit('notification:read', data));
    this.socket.on('notification:deleted', (data: { notificationId: string }) => this.emit('notification:deleted', data));
    this.socket.on('notifications:all_deleted', (data: { deletedCount: number }) => this.emit('notifications:all_deleted', data));
    this.socket.on('notifications:all_read', (data: { count: number }) => this.emit('notifications:all_read', data));
    // audit #151: live DM message channel so open DM threads update without a
    // tight poll. The backend must emit `dm:message` to the user's room for this
    // to fire; until it does, DMCoachModal keeps a slower visibility-gated poll.
    this.socket.on('dm:message', (data: any) => this.emit('dm:message', data));
    this.socket.on('account:deleted', (data: any) => this.emit('account:deleted', data));
    this.socket.on('account:reactivated', (data: any) => this.emit('account:reactivated', data));
    this.socket.on('subscription:updated', (data: any) => this.emit('subscription:updated', data));
  }

  // ────────────────────────────────────────────────────────────────
  // Client actions (emit to server)
  // ────────────────────────────────────────────────────────────────

  markAsRead(notificationId: string) {
    this.socket?.emit('mark_as_read', { notificationId });
  }

  markAllAsRead() {
    this.socket?.emit('mark_all_as_read');
  }

  deleteNotification(notificationId: string) {
    this.socket?.emit('delete_notification', { notificationId });
  }

  deleteAllNotifications() {
    this.socket?.emit('delete_all_notifications');
  }

  // ────────────────────────────────────────────────────────────────
  // Event subscription system
  // ────────────────────────────────────────────────────────────────

  subscribe(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  unsubscribe(event: string, callback: Function) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  private emit(event: string, data?: any) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners && eventListeners.size > 0) {
      eventListeners.forEach(callback => callback(data));
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  disconnect() {
    this.socket?.disconnect();
  }
}

export const socketService = new SocketService();
